import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Avatar,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Send as SendIcon,
  AttachFile as AttachFileIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  AccessTime as TimeIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { useParams, useSearchParams } from 'react-router-dom';
import socketClient, { endUserSocketClient } from '../utils/socket';
import { endUserApiClient } from '../utils/api';
import { FileViewer } from '../components/FileViewer';

interface Message {
  id: string;
  sender_type: 'end_user' | 'admin' | 'system';
  sender_name: string;
  content: string;
  created_at: string;
  is_read: boolean;
  file_url?: string | null;
  file_name?: string | null;
  message_type?: string;
}

interface ChatRoom {
  id: string;
  title: string;
  description?: string;
  status: string;
  participant_name: string;
  participant_email: string;
  created_at: string;
}

const CustomerChat: React.FC = () => {
  const { roomId } = useParams<{ roomId: string }>();
  const [searchParams] = useSearchParams();
  const urlToken = searchParams.get('token') || '';
  const { token: sessionToken } = endUserApiClient.getSession();
  const token = urlToken || sessionToken || '';

  const [chatRoom, setChatRoom] = useState<ChatRoom | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<'connected' | 'disconnected' | 'connecting'>('connecting');
  const [isInputFocused, setIsInputFocused] = useState(false); // 入力フォーカス状態を追跡
  const [roomStatus, setRoomStatus] = useState<string>('active'); // ルームステータス
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [viewerMessageId, setViewerMessageId] = useState<string | null>(null);
  const [viewerFileName, setViewerFileName] = useState<string | undefined>();

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // メッセージ一覧の最下部にスクロール
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // チャットルーム情報とメッセージを取得
  const fetchChatData = async () => {
    if (!roomId || !token) {
      setError('チャットルームIDまたはアクセストークンが無効です');
      setLoading(false);
      return;
    }

    try {
      setConnectionStatus('connecting');
      
      // トークンをセッションに保存（APIクライアントが使用）
      if (token && !sessionToken) {
        endUserApiClient.setSession(token, null);
      }

      // チャットルーム情報取得（endUserApiClient経由）
      const roomResponse = await endUserApiClient.getChatRoom(roomId);
      if (roomResponse.success && roomResponse.chatRoom) {
        setChatRoom(roomResponse.chatRoom);
        setRoomStatus(roomResponse.chatRoom.status || 'active');
      } else {
        throw new Error('チャットルームが見つかりません');
      }

      // メッセージ取得（endUserApiClient経由）
      const messagesResponse = await endUserApiClient.getMessages(roomId, { limit: 50 });
      if (messagesResponse.success) {
        const rawList: any[] = (messagesResponse as any).data || [];
        const filtered = rawList.filter((m: any, idx: number) => {
          const isSystem = m?.sender_type === 'system' || m?.message_type === 'system';
          if (!isSystem) return true;
          // 初期メッセージは表示: 先頭のシステムメッセージ、または内容的に初期案内と判断できるもの
          const looksInitial = typeof m?.content === 'string' && /チャットルーム|作成|参加|担当者が対応/.test(m.content);
          return idx === 0 || looksInitial;
        });
        setMessages(filtered);
      } else {
        throw new Error('メッセージの取得に失敗しました');
      }

      setConnectionStatus('connected');
      setError(null);
    } catch (err) {
      console.error('Chat data fetch error:', err);
      setError(err instanceof Error ? err.message : 'データの取得に失敗しました');
      setConnectionStatus('disconnected');
    } finally {
      setLoading(false);
    }
  };

  // メッセージ送信
  const handleSendMessage = async () => {
    if ((!(newMessage.trim()) && !selectedFile) || sending || !roomId || !token) return;

    setSending(true);
    try {
      let fileMeta: { url: string; fileName?: string } | null = null;
      if (selectedFile) {
        const uploaded: any = await endUserApiClient.uploadFile(selectedFile);
        if (uploaded && uploaded.success) {
          fileMeta = { url: uploaded.url, fileName: uploaded.fileName };
        } else {
          throw new Error('ファイルのアップロードに失敗しました');
        }
      }

      // endUserApiClient経由でメッセージ送信
      const response = await endUserApiClient.sendMessage(
        roomId, 
        newMessage.trim(), 
        fileMeta || undefined
      );

      if (response.success && (response as any).data) {
        setNewMessage('');
        setSelectedFile(null);
        if (fileInputRef.current) {
          // 同じファイルを再選択できるように値をクリア
          fileInputRef.current.value = '';
        }
        // 即時に自分のメッセージを反映（リアルタイム配信の対象外のため）
        setMessages(prev => [...prev, (response as any).data]);
        requestAnimationFrame(() => scrollToBottom());
      } else {
        throw new Error(response.message || 'メッセージの送信に失敗しました');
      }
    } catch (err) {
      console.error('Send message error:', err);
      setError(err instanceof Error ? err.message : 'メッセージの送信に失敗しました');
    } finally {
      setSending(false);
    }
  };

  // Enterキーは改行のみ（送信は送信ボタンのみ）
  const handleKeyPress = (event: React.KeyboardEvent) => {
    // Enterキーは改行のみ（送信は送信ボタンのみ）
    // 何もしない - デフォルトの改行動作を許可
  };

  // メッセージの送信者アイコン取得
  const getSenderIcon = (senderType: string) => {
    switch (senderType) {
      case 'admin':
        return <BusinessIcon />;
      case 'system':
        return <CheckCircleIcon />;
      default:
        return <PersonIcon />;
    }
  };

  // メッセージの送信者色取得
  const getSenderColor = (senderType: string) => {
    switch (senderType) {
      case 'admin':
        return 'primary';
      case 'system':
        return 'success';
      default:
        return 'default';
    }
  };

  // 時刻フォーマット
  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('ja-JP', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  // 初回データ取得
  useEffect(() => {
    fetchChatData();
  }, [roomId, token]);

  const isDeleted = roomStatus === 'archived';

  // メッセージ更新後のスクロール
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // 定期的なメッセージ更新（ポーリング）- 入力中は停止
  useEffect(() => {
    if (connectionStatus === 'connected' && !isInputFocused) {
      pollingIntervalRef.current = setInterval(() => {
        fetchChatData();
      }, 30000); // 30秒間隔（入力中でない場合のみ）
    } else {
      // 入力中またはオフライン時はポーリングを停止
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    }

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, [connectionStatus, isInputFocused, roomId, token]);

  // Socket.IO によるリアルタイム受信と接続状態反映
  useEffect(() => {
    if (!roomId) return;

    const handleConnected = () => setConnectionStatus('connected');
    const handleDisconnected = () => setConnectionStatus('disconnected');
    const handleNewMessage = (event: Event) => {
      const custom = event as CustomEvent<any>;
      const msg = custom.detail;
      if (!msg || msg.chat_room_id !== roomId) return;
      if (msg.sender_type === 'system' || msg.message_type === 'system') return;
      setMessages(prev => {
        if (prev.some(m => m.id === msg.id)) return prev;
        return [...prev, msg];
      });
    };

    window.addEventListener('rt:connected', handleConnected as EventListener);
    window.addEventListener('rt:disconnected', handleDisconnected as EventListener);
    window.addEventListener('rt:newMessage', handleNewMessage as EventListener);

    endUserSocketClient.connect_socket();
    endUserSocketClient.autoJoinRoom(roomId);

    return () => {
      window.removeEventListener('rt:connected', handleConnected as EventListener);
      window.removeEventListener('rt:disconnected', handleDisconnected as EventListener);
      window.removeEventListener('rt:newMessage', handleNewMessage as EventListener);
      endUserSocketClient.leaveRoom(roomId);
    };
  }, [roomId]);

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        flexDirection: 'column',
        gap: 2
      }}>
        <CircularProgress size={60} />
        <Typography variant="h6" color="text.secondary">
          チャットルームに接続中...
        </Typography>
      </Box>
    );
  }

  if (error && !chatRoom) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        p: 3
      }}>
        <Alert severity="error" sx={{ maxWidth: 500 }}>
          <Typography variant="h6" gutterBottom>
            アクセスエラー
          </Typography>
          <Typography>
            {error}
          </Typography>
          <Button 
            variant="outlined" 
            onClick={() => window.location.reload()} 
            sx={{ mt: 2 }}
            startIcon={<RefreshIcon />}
          >
            再試行
          </Button>
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      height: '100vh', 
      display: 'flex', 
      flexDirection: 'column',
      bgcolor: 'grey.50',
      position: 'relative'
    }}>
      {/* ヘッダー */}
      <Paper elevation={2} sx={{ p: 2, borderRadius: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              {chatRoom?.title || 'チャットルーム'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {chatRoom?.description}
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              icon={connectionStatus === 'connected' ? <CheckCircleIcon /> : <ErrorIcon />}
              label={connectionStatus === 'connected' ? '接続中' : '切断'}
              color={connectionStatus === 'connected' ? 'success' : 'error'}
              size="small"
            />
            <Tooltip title="更新">
              <IconButton onClick={fetchChatData} size="small">
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Paper>

      {/* メッセージ一覧 */}
      <Box sx={{ 
        flex: 1, 
        overflow: 'auto', 
        p: 2,
        pb: '140px', // 入力エリア分のスペースを確保
        display: 'flex',
        flexDirection: 'column',
        gap: 2
      }}>
        {messages.length === 0 ? (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '100%',
            flexDirection: 'column',
            gap: 2
          }}>
            <Typography variant="h6" color="text.secondary">
              まだメッセージがありません
            </Typography>
            <Typography variant="body2" color="text.secondary">
              下のメッセージ入力欄から最初のメッセージを送信してください
            </Typography>
          </Box>
        ) : (
          messages.map((message) => (
            <Box
              key={message.id}
              sx={{
                display: 'flex',
                flexDirection: message.sender_type === 'end_user' ? 'row-reverse' : 'row',
                alignItems: 'flex-start',
                gap: 1
              }}
            >
              <Avatar 
                sx={{ 
                  bgcolor: getSenderColor(message.sender_type) + '.main',
                  width: 32,
                  height: 32
                }}
              >
                {getSenderIcon(message.sender_type)}
              </Avatar>
              
              <Box sx={{ 
                maxWidth: '70%',
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5
              }}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1,
                  flexDirection: message.sender_type === 'end_user' ? 'row-reverse' : 'row'
                }}>
                  <Typography variant="caption" color="text.secondary">
                    {message.sender_name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <TimeIcon sx={{ fontSize: 12 }} />
                    {formatTime(message.created_at)}
                  </Typography>
                </Box>
                
                <Paper
                  elevation={1}
                  sx={{
                    p: 1.5,
                    bgcolor: message.sender_type === 'end_user' ? 'primary.main' : 'white',
                    color: message.sender_type === 'end_user' ? 'white' : 'text.primary',
                    borderRadius: 2,
                    wordBreak: 'break-word'
                  }}
                >
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {message.content.split(/(https?:\/\/[^\s]+)/g).map((part, idx) => (
                      /^https?:\/\//.test(part) ? (
                        <a key={idx} href={part} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>
                          {part}
                        </a>
                      ) : (
                        <span key={idx}>{part}</span>
                      )
                    ))}
                  </Typography>
                  {/* 添付ファイル */}
                  {message.file_url && (() => {
                    const isImage = /\.(png|jpe?g|gif|webp)$/i.test(message.file_name || message.file_url || '');
                    const previewUrl = `/api/files/${message.id}/preview?token=${encodeURIComponent(token)}`;
                    const euExpiryDays = 7;
                    const uploadDate = new Date(message.created_at);
                    const expiryDate = new Date(uploadDate.getTime() + euExpiryDays * 24 * 60 * 60 * 1000);
                    const diffMs = expiryDate.getTime() - Date.now();
                    const isExpired = diffMs <= 0;
                    const remainDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
                    const expiryFormatted = expiryDate.toLocaleDateString('ja-JP', { year: 'numeric', month: '2-digit', day: '2-digit' });
                    const expiryLabel = isExpired
                      ? `ダウンロード期限切れ（${expiryFormatted}）`
                      : `ダウンロード期限: ${expiryFormatted}（残${remainDays}日）`;
                    return (
                      <>
                        {isImage ? (
                          <Box
                            sx={{ mt: 1, maxWidth: '240px', borderRadius: 1, overflow: 'hidden', cursor: 'pointer', '&:hover': { opacity: 0.9 } }}
                            onClick={() => { setViewerMessageId(message.id); setViewerFileName(message.file_name || undefined); }}
                          >
                            <img
                              src={previewUrl}
                              alt={message.file_name || '添付画像'}
                              style={{ width: '100%', height: 'auto', display: 'block' }}
                              onError={(e) => { e.currentTarget.style.display = 'none'; }}
                            />
                          </Box>
                        ) : (
                          <Box
                            sx={{ mt: 1, p: 1, bgcolor: message.sender_type === 'end_user' ? 'rgba(255,255,255,0.15)' : '#f5f5f5', borderRadius: 1, cursor: 'pointer', '&:hover': { bgcolor: message.sender_type === 'end_user' ? 'rgba(255,255,255,0.25)' : '#eee' } }}
                            onClick={() => { setViewerMessageId(message.id); setViewerFileName(message.file_name || undefined); }}
                          >
                            <Typography variant="caption" sx={{ color: message.sender_type === 'end_user' ? 'inherit' : 'primary.main', textDecoration: 'underline' }}>
                              📎 {message.file_name || '添付ファイル'}
                            </Typography>
                          </Box>
                        )}
                        <Typography
                          variant="caption"
                          sx={{
                            display: 'block',
                            mt: 0.3,
                            fontSize: '0.7rem',
                            color: isExpired
                              ? '#d32f2f'
                              : message.sender_type === 'end_user' ? 'rgba(255,255,255,0.6)' : '#999',
                          }}
                        >
                          {expiryLabel}
                        </Typography>
                      </>
                    );
                  })()}
                </Paper>
              </Box>
            </Box>
          ))
        )}
        <div ref={messagesEndRef} />
      </Box>

      {/* 固定入力エリア */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 10,
        }}
      >
        {/* エラー表示 */}
        {error && (
          <Alert severity="error" sx={{ m: 2, mb: 0 }}>
            {error}
          </Alert>
        )}
        {isDeleted && (
          <Alert severity="warning" sx={{ m: 2, mb: 0 }}>
            このチャットは削除されたため、受付できません。
          </Alert>
        )}

        {/* メッセージ入力 */}
        <Paper elevation={3} sx={{ p: 2, borderRadius: 0, boxShadow: '0 -2px 8px rgba(0,0,0,0.1)' }}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
            <IconButton 
              size="small" 
              onClick={() => fileInputRef.current?.click()}
              disabled={sending || connectionStatus !== 'connected' || roomStatus === 'completed' || isDeleted}
            >
              <AttachFileIcon />
            </IconButton>
            <input 
              type="file" 
              hidden 
              ref={fileInputRef} 
              onChange={(e) => setSelectedFile(e.target.files?.[0] || null)} 
            />
            <TextField
              fullWidth
              multiline
              maxRows={4}
              placeholder={
                isDeleted
                  ? 'このチャットは削除されました（送信不可）'
                  : roomStatus === 'completed' 
                    ? 'このチャットは完了しています' 
                    : 'メッセージを入力してください...'
              }
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              onFocus={() => setIsInputFocused(true)} // フォーカス時にポーリング停止
              onBlur={() => setIsInputFocused(false)}  // フォーカス解除時にポーリング再開
              disabled={sending || connectionStatus !== 'connected' || roomStatus === 'completed' || isDeleted}
              variant="outlined"
              size="small"
              inputProps={{
                style: { fontSize: 16 },
                enterKeyHint: 'send'
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 3,
                  bgcolor: 'background.paper'
                },
                '& .MuiInputBase-input': {
                  fontSize: '16px',
                  WebkitTextSizeAdjust: '100%',
                  lineHeight: 1.4
                },
                '& textarea': {
                  fontSize: '16px',
                  WebkitTextSizeAdjust: '100%',
                  lineHeight: 1.4
                }
              }}
            />
            <Button
              variant="contained"
              onClick={handleSendMessage}
              disabled={(!newMessage.trim() && !selectedFile) || sending || connectionStatus !== 'connected' || roomStatus === 'completed' || isDeleted}
              sx={{ minWidth: 60, height: 40 }}
            >
              {sending ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <SendIcon />
              )}
            </Button>
          </Box>
          {selectedFile && (
            <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip
                label={`添付: ${selectedFile.name}`}
                onDelete={() => {
                  setSelectedFile(null);
                  if (fileInputRef.current) fileInputRef.current.value = '';
                }}
                deleteIcon={<CloseIcon />}
                variant="outlined"
                size="small"
              />
              <Typography variant="caption" color="text.secondary">
                送信前に削除できます
              </Typography>
            </Box>
          )}
          
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Enterキーで送信 • Shift+Enterで改行
          </Typography>
        </Paper>
      </Box>

      {viewerMessageId && (
        <FileViewer
          open={!!viewerMessageId}
          onClose={() => setViewerMessageId(null)}
          messageId={viewerMessageId}
          fileName={viewerFileName}
        />
      )}
    </Box>
  );
};

export default CustomerChat;
